import { alpha } from "@mui/material";
import { LinkProps } from "@mui/material/Link";
import { blue, blueGrey } from "@mui/material/colors";
import {
  createTheme as MuiCreateTheme,
  ThemeOptions,
} from "@mui/material/styles";
import { ThemeExtension, ThemeOptionsExtension } from "common";
import { forwardRef } from "react";
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";
//@ts-ignore
//@ts-ignore
//@ts-ignore
const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  /*if (href.toString().includes("://")) {
    return (
      <Link ref={ref} href={href.toString()} {...omit(other, "component")} />
    );
  }*/
  return <RouterLink ref={ref} to={href} {...other} />;
});

declare module "@mui/material/styles" {
  interface Theme extends ThemeExtension {
    test: {
      danger: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions extends ThemeOptionsExtension {
    test?: {
      danger?: string;
    };
  }
}

export default function createTheme(baseTheme: ThemeOptions) {
  let theme = MuiCreateTheme(baseTheme, {
    palette: {
      primary: {
        light: blue[100],
        main: blue[600],
        dark: blue[800],
        contrastText: "#FFFFFF",
      },
      secondary: {
        light: blueGrey[50],
        main: blueGrey[300],
        dark: blueGrey[500],
        contrastText: "#FFFFFF",
      },
      text: {
        primary: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
        secondary: baseTheme.palette?.mode === "dark" ? "#b0b0b0" : "#666666",
      },
      action: {
        active: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1976d2",
      },
      background: {
        paper: baseTheme.palette?.mode === "dark" ? "#1e1e1e" : "#FFFFFF",
        default: baseTheme.palette?.mode === "dark" ? "#121212" : "#f5f7fa",
      },
    },
    typography: {
      fontFamily: [
        '"Inter"',
        '"Roboto"',
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 400,
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: 1.2,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      h2: {
        fontWeight: 600,
        fontSize: "2rem",
        lineHeight: 1.3,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.3,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.4,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.4,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      h6: {
        fontWeight: 600,
        fontSize: "1.1rem",
        lineHeight: 1.5,
        color: baseTheme.palette?.mode === "dark" ? "#FFF" : "#1a1a1a",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
      },
    },
    shape: {
      borderRadius: 0,
    },
  } as ThemeOptions);
  theme = MuiCreateTheme(theme, {
    extraComponents: {
      MuiPageHeader: {
        defaultProps: {
          sx: { color: theme.palette.primary.main },
        },
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            height: "100%",
            fontSize: "14px",
          },
          body: {
            height: "100%",
            display: "flex",
            backgroundColor: theme.palette.background.default,
          },
          "#root": {
            display: "flex",
            flex: 1,
            backgroundColor: theme.palette.background.default,
          },
          ".MuiLayoutContent": {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            marginRight: theme.spacing(2),
            [theme.breakpoints.down("md")]: {
              marginRight: theme.spacing(2),
              marginLeft: theme.spacing(2),
            },
            [theme.breakpoints.down("sm")]: {
              marginRight: 0,
              marginLeft: 0,
            },
            marginTop: theme.spacing(2),
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          },
          ".tabler-icon": {
            width: "20px",
            height: "20px",
          },
          ".MuiOutlinedInput-input:-webkit-autofill": {
            boxShadow: `0 0 0 100px ${theme.palette.background.default} inset`,
            //background: "none!important",
            //backgroundColor: `${theme.palette.background.default}!important`,
          },
          /*".MuiOutlinedInput-input:-internal-autofill-selected": {
            boxShadow: `0 0 0 100px transparent inset`,
            background: "none!important",
            backgroundColor: "transparent!important",
          },*/
          /*"input:-internal-autofill-selected": {
            boxShadow: `0 0 0 100px transparent inset`,
            background: "none!important",
            backgroundColor: "transparent!important",
          },*/
          ".MuiCardHeader-root + .MuiCardContent-root": {
            paddingTop: 0,
          },
          '.MuiDataGrid-main>div[style*="z-index: 100000"]': {
            display: "none!important",
          },
          ".MuiSidebar>.MuiPaper-root": {
            backgroundColor: "transparent",
          },
          ".MuiSidebar .MuiListItem-root": {
            padding: 0,
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
          },
          ".MuiSidebar .MuiList-root": {
            padding: 0,
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
          },
          ".MuiSidebar .MuiListItemText-primary": {
            color: theme.palette.primary.main,
            fontWeight: 400,
          },
          ".MuiSidebar .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
            minWidth: "40px",
          },
          ".MuiSidebar .MuiMenuItem-root": {
            padding: 0,
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
            paddingTop: 0,
            paddingBottom: 0,
            minHeight: "auto",
          },
          ".MuiDialog-root .MuiPageHeader": {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(2),
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            transition:
              "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
            "&:hover": {
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              borderColor: alpha(theme.palette.primary.main, 0.2),
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 0,
          },
          labelIcon: {
            minHeight: 0,
            "& .MuiTab-iconWrapper": {
              marginBottom: "4px",
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderBottom: "1px solid",
            borderBottomColor: alpha(theme.palette.primary.main, 0.1),
            marginBottom: theme.spacing(2),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            textTransform: "none",
            fontWeight: 600,
            padding: "8px 20px",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            },
          },
          containedPrimary: {
            "&:not(.Mui-disabled)": {
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          size: "small",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              transition:
                "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                boxShadow: `0 0 0 3px ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
              },
            },
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          density: "compact",
          buttonSize: "small",
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          inputSizeSmall: {
            //fontSize: "0.875rem",
          },
          root: {
            "input.MuiInputBase-inputAdornedEnd": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
            "input.MuiInputBase-inputAdornedStart": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            //fontSize: "0.875rem",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 0,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            boxShadow: "none",
            backgroundColor: "transparent",
          },
        },
      },
      MuiLink: {
        defaultProps: {
          component: LinkBehavior,
        } as LinkProps,
      },
      MuiDialog: {
        styleOverrides: {
          scrollPaper: {
            alignItems: "baseline",
          },
        },
        /*defaultProps: {
          disableEnforceFocus: true,
        },*/
      },
    },
  } as ThemeOptions);
  console.log(theme);
  return theme;
}
